import axios from "axios";
import { getToken } from "../utils/authToken";

// Create axios instance with base URL
const axiosInstance = axios.create({
  baseURL: "https://api.mazraaonline.com/api",
  timeout: 30000, // 30 seconds timeout
});

// Request cache for GET requests (simple in-memory cache)
const requestCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 100; // Maximum cache entries

// Cleanup function to remove expired cache entries
const cleanupCache = () => {
  if (requestCache.size > MAX_CACHE_SIZE) {
    const now = Date.now();
    for (const [key, value] of requestCache.entries()) {
      if (now - value.timestamp > CACHE_DURATION) {
        requestCache.delete(key);
      }
    }
  }
};

// Add request interceptor to include token in headers
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Mark GET requests for caching
    if (config.method === 'get') {
      const cacheKey = `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
      const cached = requestCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        // Mark as cached to skip actual request
        config.__fromCache = true;
        config.__cachedData = cached.data;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for caching GET responses
axiosInstance.interceptors.response.use(
  (response) => {
    const config = response.config;
    
    // Return cached data immediately if available
    if (config.__fromCache && config.__cachedData) {
      return {
        ...response,
        data: config.__cachedData,
        __fromCache: true
      };
    }
    
    // Cache successful GET responses
    if (config.method === 'get' && response.status === 200) {
      const cacheKey = `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
      requestCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });

      // Clean old cache entries periodically (keep cache size manageable)
      cleanupCache();
    }

    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axiosInstance;

